import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, Typography, Divider, IconButton, CircularProgress,
  Alert, Skeleton, Accordion, AccordionSummary, AccordionDetails,
  FormControlLabel, Switch
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useCreatePracticeCollectionForm } from '../../hooks/useCreatePracticeCollectionForm';
import MarginBottomContainer from '../../../../components/containers/MarginBottomContainer';
import ElevatedContentCard from '../../../../components/containers/ElevatedContentCard';

const CreatePracticeCollection = ({ onCancel }) => {
  const navigate = useNavigate();
  const {
    creating,
    formValues,
    handleChange,
    handleSubmit,
    loading,
    error,
    config
  } = useCreatePracticeCollectionForm();

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const result = await handleSubmit(event);
    if (result && result.id) {
      onCancel();
      navigate(`/practice-collections/requests/${result.id}`);
    }
  };

  return (
    <MarginBottomContainer>
      <ElevatedContentCard>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2" fontWeight="600">
            Create New Practice Collection
          </Typography>
          <IconButton onClick={onCancel} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={56} /></Grid>
              <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={56} /></Grid>
              <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={56} /></Grid>
              <Grid item xs={12}><Skeleton variant="rectangular" height={120} /></Grid>
            </Grid>
          </Box>
        ) : (
          <form onSubmit={handleFormSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formValues.isPathway || false}
                      onChange={handleChange}
                      name="isPathway"
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight="600">Part of a Pathway</Typography>
                      <Typography variant="caption" color="textSecondary">Mark this collection as a part of a learning pathway (forces High School and Pathway type)</Typography>
                    </Box>
                  }
                  disabled={creating}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="school-level-label">School Level</InputLabel>
                  <Select
                    labelId="school-level-label"
                    id="schoolLevel"
                    name="schoolLevel"
                    value={formValues.schoolLevel}
                    onChange={handleChange}
                    label="School Level"
                    disabled={creating || formValues.isPathway}
                  >
                    {config?.schoolLevels?.map((level) => (
                      <MenuItem key={level.value} value={level.value}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="collection-type-label">Collection Type</InputLabel>
                  <Select
                    labelId="collection-type-label"
                    id="type"
                    name="type"
                    value={formValues.type}
                    onChange={handleChange}
                    label="Collection Type"
                    disabled={creating || formValues.isPathway}
                  >
                    <MenuItem value="FICTION">Fiction</MenuItem>
                    <MenuItem value="NON_FICTION">Non-Fiction</MenuItem>
                    {formValues.isPathway && (
                      <MenuItem value="PATHWAY">Pathway Collection</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  id="numberOfFiction"
                  name="numberOfFiction"
                  label="Fiction Stories Count"
                  type="number"
                  value={formValues.numberOfFiction}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 10 }}
                  disabled={creating}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  id="numberOfNonFiction"
                  name="numberOfNonFiction"
                  label="Non-Fiction Stories Count"
                  type="number"
                  value={formValues.numberOfNonFiction}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 10 }}
                  disabled={creating}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="sourceMaterial"
                  name="sourceMaterial"
                  label="Source Material / Topic"
                  multiline
                  rows={6}
                  value={formValues.sourceMaterial}
                  onChange={handleChange}
                  disabled={creating}
                  placeholder="Paste source material text or specify a topic to generate the collection from."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="userNote"
                  name="userNote"
                  label="User Note (Optional)"
                  multiline
                  rows={3}
                  value={formValues.userNote || ''}
                  onChange={handleChange}
                  disabled={creating}
                  placeholder="Enter any specific instructions or requirements to guide the generation (e.g. key characters, themes, or concepts to focus on)."
                />
              </Grid>

              <Grid item xs={12}>
                <Accordion sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="advanced-options-content"
                    id="advanced-options-header"
                  >
                    <Typography fontWeight="500">Advanced Parameters (Overrides)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel id="category-label">Category Override</InputLabel>
                          <Select
                            labelId="category-label"
                            id="category"
                            name="category"
                            value={formValues.category}
                            onChange={handleChange}
                            label="Category Override"
                            disabled={creating}
                          >
                            <MenuItem value=""><em>None (Use Default)</em></MenuItem>
                            {config?.readingCategories?.map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel id="content-style-label">Content Style Override</InputLabel>
                          <Select
                            labelId="content-style-label"
                            id="paragraphContentStyle"
                            name="paragraphContentStyle"
                            value={formValues.paragraphContentStyle}
                            onChange={handleChange}
                            label="Content Style Override"
                            disabled={creating}
                          >
                            {config?.contentStyles?.map((style) => (
                              <MenuItem key={style.name} value={style.name}>
                                {style.description}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          id="totalWords"
                          name="totalWords"
                          label="Total Words"
                          type="number"
                          value={formValues.totalWords}
                          onChange={handleChange}
                          inputProps={{ min: 50, max: 5000 }}
                          disabled={creating}
                          helperText={`Default for ${formValues.schoolLevel} will be overridden`}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          id="decodeWords"
                          name="decodeWords"
                          label="Decode Words"
                          type="number"
                          value={formValues.decodeWords}
                          onChange={handleChange}
                          inputProps={{ min: 1, max: 100 }}
                          disabled={creating}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          id="questionCount"
                          name="questionCount"
                          label="Questions Count"
                          type="number"
                          value={formValues.questionCount}
                          onChange={handleChange}
                          inputProps={{ min: 1, max: 20 }}
                          disabled={creating}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          id="optionCount"
                          name="optionCount"
                          label="Options per Question"
                          type="number"
                          value={formValues.optionCount}
                          onChange={handleChange}
                          inputProps={{ min: 2, max: 6 }}
                          disabled={creating}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
              <Button variant="outlined" onClick={onCancel} disabled={creating}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  creating || 
                  !formValues.schoolLevel || 
                  !formValues.sourceMaterial || 
                  (parseInt(formValues.numberOfFiction || 0, 10) + parseInt(formValues.numberOfNonFiction || 0, 10) === 0)
                }
                startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              >
                {creating ? 'Creating...' : 'Generate Collection'}
              </Button>
            </Box>
          </form>
        )}
      </ElevatedContentCard>
    </MarginBottomContainer>
  );
};

export default CreatePracticeCollection;
